import { AxiosError } from 'axios';
import { ZodError } from 'zod';

/**
 * Error types that can be displayed by the ErrorHandler component
 */
export type ErrorTypes = (
    | { type: 'string'; value: string }
    | { type: 'errors'; message: string; errors: Record<string, string[]> }
    | { type: 'none' }
    | { type: 'redirect' }
    | { type: 'notfound' }
    | { type: 'unknown' }
    | { type: 'exists' }
) &
    (
        | {
              reportable?: false;
          }
        | {
              reportable: true;
              report: any;
          }
    );

/**
 * Props for error computation
 */
export interface ErrorComputationProps {
    type?: 'upload';
    redirect?: boolean;
}

/**
 * Computes any error into a standardized format that can be displayed by ErrorHandler
 *
 * @param error - The error to compute (can be any type)
 * @param props - Additional props for error computation
 * @returns A standardized error type that can be displayed
 */
export function computeError(error: any, props: ErrorComputationProps = {}): ErrorTypes {
    if (!error) {
        return { type: 'none' };
    }

    if (props.type === 'upload') {
        return { type: 'string', value: 'An error occurred while uploading the file.' };
    }

    if (
        (typeof error === 'object' &&
            'originalResponse' in error &&
            typeof error.originalResponse === 'object' &&
            error.originalResponse?.getStatus() === 429) ||
        (error instanceof Error && error.message.toLowerCase().includes('Too many requests'))
    ) {
        return { type: 'string', value: 'Too many requests. Please try again later.' };
    }

    if (typeof error === 'string') {
        return { type: 'string', value: error };
    }

    if (error instanceof AxiosError) {
        return computeAxiosError(error, props);
    }

    if (error instanceof ZodError) {
        return {
            type: 'string',
            value: 'An error occurred that cannot be fixed immediately. It is recommended to report it.',
            reportable: true,
            report: error,
        };
    }

    if (error instanceof Error) {
        return { type: 'string', value: error.message };
    }

    return { type: 'unknown' };
}

export const computeErrorMessage = (error: any, props: ErrorComputationProps = {}): string | undefined => {
    const computed = computeError(error, props);
    switch (computed.type) {
        case 'string':
            return computed.value;
        case 'none':
            return undefined;
        case 'errors':
            return computed.message;
        case 'exists':
            return 'An item like this already exists.';
        case 'unknown':
            return 'An unknown error occurred.';
        case 'redirect':
            return "The item you're looking for could not be found.";
        case 'notfound':
            return "The item you're looking for could not be found.";
        default:
            return undefined;
    }
};

/**
 * Computes AxiosError into a standardized format
 */
function computeAxiosError(error: AxiosError, props: ErrorComputationProps): ErrorTypes {
    const responseData = error.response?.data as any;

    // Handle OTP-specific errors
    if (responseData?.code === 'otp') {
        if (error.response?.status === 403) {
            return { type: 'string', value: responseData?.message ?? 'You have entered an incorrect OTP.' };
        }
        if (error.response?.status === 410) {
            return { type: 'string', value: 'OTP has expired or too many failed attempts. Please refresh.' };
        }
        if (error.response?.status === 429) {
            return { type: 'string', value: 'OTP cannot be refreshed right now. Try again later.' };
        }
    }

    // Handle validation errors
    if (responseData?.code === 'validation_failed' && responseData?.errors) {
        return { type: 'errors', message: responseData.message, errors: responseData.errors };
    }

    // Handle 400 errors
    if (error.response?.status === 400 && responseData?.message) {
        if (responseData.code === 'exists') {
            return { type: 'exists' };
        }
        return { type: 'string', value: responseData?.message, reportable: true, report: error };
    }

    // Handle 403 errors
    if (error.response?.status === 403) {
        return {
            type: 'string',
            value:
                responseData?.message ??
                (error.config?.method === 'get'
                    ? 'You do not have the permission to view this.'
                    : 'You do not have the permission to perform this action.'),
        };
    }

    // Handle 404 errors
    if (error.response?.status === 404) {
        if (props.redirect) {
            return { type: 'redirect' };
        }
        return { type: 'notfound' };
    }

    if (error.response?.status === 429) {
        return { type: 'string', value: 'Too many requests. Please try again later.' };
    }

    // Handle 422 errors
    if (error.response?.status === 422 && typeof responseData?.message === 'string') {
        return { type: 'string', value: responseData.message };
    }

    // Default AxiosError handling
    return {
        type: 'string',
        value: 'An error occurred while communicating with the server. Please try again.',
        reportable: true,
        report: error,
    };
}

/**
 * Checks if an error is reportable
 *
 * @param error - The computed error
 * @param canReport - Whether reporting is allowed
 * @returns True if the error can be reported
 */
export function isErrorReportable(error: ErrorTypes, canReport: boolean = false): boolean {
    return canReport && error.reportable === true;
}

/**
 * Gets a user-friendly error message from any error
 *
 * @param error - The error to get a message from
 * @param props - Additional props for error computation
 * @returns A user-friendly error message
 */
export function getErrorMessage(error: any, props: ErrorComputationProps = {}): string {
    const computed = computeError(error, props);

    switch (computed.type) {
        case 'string':
            return computed.value;
        case 'errors':
            return computed.message;
        case 'exists':
            return 'An item like this already exists.';
        case 'unknown':
            return 'An unknown error occurred.';
        case 'redirect':
        case 'notfound':
            return "The item you're looking for could not be found.";
        case 'none':
        default:
            return '';
    }
}

/**
 * Checks if an error is a specific type
 *
 * @param error - The computed error
 * @param type - The error type to check for
 * @returns True if the error is of the specified type
 */
export function isErrorType(error: ErrorTypes, type: ErrorTypes['type']): boolean {
    return error.type === type;
}

/**
 * Checks if an error is a network/communication error
 *
 * @param error - The computed error
 * @returns True if the error is a network error
 */
export function isNetworkError(error: ErrorTypes): boolean {
    return (
        error.type === 'string' &&
        error.value === 'An error occurred while communicating with the server. Please try again.'
    );
}

/**
 * Checks if an error is a validation error
 *
 * @param error - The computed error
 * @returns True if the error is a validation error
 */
export function isValidationError(error: ErrorTypes): boolean {
    return error.type === 'errors';
}

/**
 * Checks if an error is a permission error
 *
 * @param error - The computed error
 * @returns True if the error is a permission error
 */
export function isPermissionError(error: ErrorTypes): boolean {
    return error.type === 'string' && (error.value.includes('permission') || error.value.includes('not have'));
}
